import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import SyncIcon from "@mui/icons-material/Sync";
import FileUploadIcon from "@mui/icons-material/FileUpload";

import { toast } from "react-toastify";
import EquipmentMasterCatalogApi from "../../../../../infrastructure/api/equipmentMasterCatalog";
import EquipmentSeedImportDialog from "./EquipmentSeedImportDialog";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";
import EquipmentPublicCodeChip from "../../shared/equipmentCatalog/EquipmentPublicCodeChip";

const STATUS_OPTIONS = [
  { value: "", label: "همه وضعیت‌ها" },
  { value: 1, label: "پیش‌نویس" },
  { value: 2, label: "در انتظار بررسی" },
  { value: 3, label: "تأییدشده" },
  { value: 4, label: "نیازمند اصلاح" },
  { value: 5, label: "ردشده" },
  { value: 6, label: "منسوخ‌شده" },
  { value: 7, label: "غیرفعال" },
];

const statusColor = (status) => {
  switch (status) {
    case 3:
      return "success";
    case 2:
      return "primary";
    case 4:
      return "warning";
    case 5:
    case 6:
    case 7:
      return "default";
    default:
      return "info";
  }
};

const EquipmentMasterCatalogPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ term: "", categoryId: "", status: "" });
  const [loading, setLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await EquipmentMasterCatalogApi.searchItems({
        term: filters.term || "",
        categoryId: filters.categoryId || null,
        status: filters.status === "" ? null : Number(filters.status),
        includeInactive: true,
        pageNumber: 1,
        pageSize: 100,
      });
      const items = res?.data?.items ?? res?.data?.Items ?? [];
      setRows(items);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    EquipmentMasterCatalogApi.getCategories(true).then((r) =>
      setCategories(r?.data || [])
    );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onApprove = async (row) => {
    const res = await EquipmentMasterCatalogApi.approveItem(row.id);
    if (res.ok) {
      toast.success(`تجهیز تأیید شد؛ کد عمومی: ${res.data?.publicCode || ""}`);
      load();
    } else toast.error(res.message || "خطا در تأیید");
  };

  const onDeprecate = async (row) => {
    const res = await EquipmentMasterCatalogApi.deprecateItem(row.id, "منسوخ‌سازی توسط اپراتور");
    if (res.ok) {
      toast.success("تجهیز منسوخ شد");
      load();
    } else toast.error(res.message || "خطا در منسوخ‌سازی");
  };

  const headCells = [
    {
      id: "publicCode",
      label: "کد عمومی",
      render: (value) => <EquipmentPublicCodeChip code={value} />,
    },
    { id: "shortTitle", label: "عنوان" },
    { id: "categoryTitle", label: "دسته" },
    { id: "brandName", label: "برند" },
    { id: "modelName", label: "مدل" },
    {
      id: "statusTitle",
      label: "وضعیت",
      render: (value, row) => (
        <Chip size="small" color={statusColor(row.status)} label={row.statusTitle} />
      ),
    },
  ];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="کاتالوگ مرجع تجهیزات"
            action={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<FileUploadIcon />}
                  onClick={() => setImportOpen(true)}
                  sx={{ mr: 1 }}
                >
                  ایمپورت Seed
                </Button>
                <Link to={"create"}>
                  <Button variant="contained" color="primary" startIcon={<AddIcon />}>
                    ایجاد تجهیز
                  </Button>
                </Link>
                <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 1 }} />
                <IconButton color="primary" onClick={load}>
                  <SyncIcon />
                </IconButton>
              </Box>
            }
          />
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="جستجو (کد/عنوان/مدل/برند/نام مستعار)"
                  value={filters.term}
                  onChange={(e) => setFilters((f) => ({ ...f, term: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="دسته"
                  value={filters.categoryId}
                  onChange={(e) => setFilters((f) => ({ ...f, categoryId: e.target.value }))}
                >
                  <MenuItem value="">همه دسته‌ها</MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.titleFa} ({c.codePrefix})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="وضعیت"
                  value={filters.status}
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <EnhancedTable
              rowKey={"id"}
              rows={rows}
              loading={loading}
              headCells={headCells}
              pageSize={100}
              actions={[
                {
                  icon: <EditIcon sx={{ width: "1rem", height: "1rem" }} />,
                  onClick: (row) => navigate(`${row.id}`),
                  color: "primary",
                  tooltip: "ویرایش",
                },
                {
                  icon: <CheckCircleIcon sx={{ width: "1rem", height: "1rem" }} />,
                  onClick: onApprove,
                  color: "success",
                  tooltip: "تأیید و صدور کد",
                },
                {
                  icon: <BlockIcon sx={{ width: "1rem", height: "1rem" }} />,
                  onClick: onDeprecate,
                  color: "secondary",
                  tooltip: "منسوخ‌سازی",
                },
              ]}
            />
          </CardContent>
        </Card>
      </Grid>
      <EquipmentSeedImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onCommitted={load}
      />
    </Grid>
  );
};

export default EquipmentMasterCatalogPage;
